import { useEffect, useRef } from "react";

import api from "../utilities/api.js";
import { useAuthStore } from "../store/authStore.js";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // matches the server cookie/JWT expiry
const PING_INTERVAL_MS = 5 * 60 * 1000; // checks well inside the idle window for safety margin
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll"];

// Keeps an active session alive by pinging /auth/check-auth (which slides the
// cookie forward as a side effect of passing through verifyToken) as long as
// there has been user activity since the last tick. A genuinely idle user is
// left un-pinged so the session expires naturally.
export function useSessionHeartbeat() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!isAuthenticated) return;

    lastActivityRef.current = Date.now();
    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true }),
    );

    const interval = setInterval(async () => {
      if (!useAuthStore.getState().isAuthenticated) return;
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) return;

      try {
        const response = await api.get("/auth/check-auth");
        if (!response.data.success) {
          useAuthStore.getState().flagSessionExpired();
        }
      } catch {
        useAuthStore.getState().flagSessionExpired();
      }
    }, PING_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
      clearInterval(interval);
    };
  }, [isAuthenticated]);
}
